import i18n from 'i18next';
import DOMPurify from 'dompurify';
import { SetErrorStateType, SnackMessageType } from '../models/common';
import { QcsError } from '@api/models/QcsError';
import { QcsValidationResponse } from '@api/models/common/QcsValidationResponse';

// Allowed domains for links in error messages
const ALLOWED_LINK_DOMAINS = ['safe4entry.com', 'localhost', 's4e.cz'];

// Helper to check if URL is from an allowed domain
const isAllowedUrl = (url: string): boolean => {
  try {
    const parsed = new URL(url, window.location.origin);
    return ALLOWED_LINK_DOMAINS.some((domain) => parsed.hostname === domain || parsed.hostname.endsWith(`.${domain}`));
  } catch {
    return false; // Invalid URL
  }
};

// Helper to detect if a string contains HTML
const containsHtml = (str: string): boolean => {
  return /<[^>]*>/g.test(str);
};

// Component for sanitized HTML with link validation
const SanitizedHtml = ({ html }: { html: string }) => {
  // Configure DOMPurify to validate links
  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    if (node.tagName === 'A') {
      const href = node.getAttribute('href');
      if (href && !isAllowedUrl(href)) {
        // Remove href from disallowed links, keeping the text
        node.removeAttribute('href');
      } else if (href) {
        // Add security attributes to allowed links
        node.setAttribute('target', '_blank');
        node.setAttribute('rel', 'noopener noreferrer');
      }
    }
  });

  const sanitized = DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ['a', 'strong', 'em', 'b', 'i', 'ul', 'li', 'br'],
    ALLOWED_ATTR: ['href'],
  });

  // Clean up hook to avoid memory leaks
  DOMPurify.removeHook('afterSanitizeAttributes');

  return <span dangerouslySetInnerHTML={{ __html: sanitized }} />;
};

export const getErrorMessage = (err: any, defaultMessage?: string | null) => {
  const code = getApiErrorCode(err);
  if (code) {
    const trans = 'errorCodes.' + code;

    if (i18n.exists(trans)) {
      //CSV special error.
      if (code === '007' && err?.response?.data?.column) {
        return `${i18n.t(trans)} ${i18n
          .t(trans + '_detail')
          .replaceAll('{column}', err.response.data.column)
          .replaceAll('{row}', err.response.data.row)}`;
      }

      //PIN special error.
      if (code === '035') {
        const exceptions: QcsError[] = (err.response.data as QcsValidationResponse).exceptions ?? [];
        if (exceptions.length) {
          return (
            <div>
              {i18n.t(trans)}
              <ul style={{ paddingLeft: '2rem' }}>
                {exceptions.map((x, i) => (
                  <li key={i}>
                    <strong>{i18n.t(`visitation.accessPointLog.${x.actionRequiredFrom.toLowerCase()}`)}</strong>:{' '}
                    {getTranslatedError(x.code, x.parameters)}
                  </li>
                ))}
              </ul>
            </div>
          );
        }
      }

      //Missing column in csv.
      if (code === '036' && err?.response?.data?.row) {
        return `${i18n.t('errorCodes.007')} ${i18n.t(trans).replaceAll('{row}', err.response.data.row)}`;
      }

      // Generic translation with HTML support
      const params = err?.response?.data?.parameters || {};
      return getTranslatedError(code, params);
    }
  }

  if (err?.qcsCustomError) {
    const code = err?.qcsCustomError;
    const trans = 'errorCustomCodes.' + code;

    if (i18n.exists(trans)) {
      return i18n.t(trans);
    }
  }

  return i18n.t(defaultMessage ?? 'common.unknownError');
};

export const setErrorState = (err: any, setState: SetErrorStateType, defaultMessage?: string | null) => {
  const res = getErrorMessage(err, defaultMessage);
  setState(res);
  return res;
};

export const setErrorSnacks = (err: any, enqueueErrorSnackbar: (message: SnackMessageType) => void, defaultMessage?: string | null) => {
  console.error(err);
  const res = getErrorMessage(err, defaultMessage);
  enqueueErrorSnackbar(res);
  return res;
};

export const setErrorStateSnacks = (
  err: any,
  setState: SetErrorStateType,
  enqueueErrorSnackbar: (message: SnackMessageType) => void,
  defaultMessage?: string | null,
) => {
  const res = getErrorMessage(err, defaultMessage);
  setState(res);
  enqueueErrorSnackbar(res);
  return res;
};

export const getCustomError = (code: string) => ({
  qcsCustomError: code,
});

/**
 * Backend error code (e.g. from a {@link QcsErrorCode}) carried in the response body, if any.
 */
export const getApiErrorCode = (err: any): string | undefined => err?.response?.data?.code;

export const getTranslatedError = (code: string, parameters?: Map<string, any> | Record<string, any>) => {
  const params = parameters instanceof Map ? Object.fromEntries(parameters) : parameters || {};
  const translated = i18n.t(`errorCodes.${code}`, params) as string;

  // If the translated string contains HTML, return sanitized JSX
  if (containsHtml(translated)) {
    return <SanitizedHtml html={translated} />;
  }

  // Otherwise return plain string
  return translated;
};
